'use client';

import { useState, useId } from 'react';
import {
  BookOpen,
  ChevronDown,
  DollarSign,
  HelpCircle,
  School,
  Search,
  SearchX,
  UserPlus,
} from 'lucide-react';
import faqDataJson from '@/data/faq.json';

type FAQCategory = 'all' | 'inscription' | 'tarifs' | 'cours' | 'pratique';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: Exclude<FAQCategory, 'all'>;
}

const iconMap = {
  BookOpen,
  UserPlus,
  DollarSign,
  School,
  HelpCircle,
};

const categories = faqDataJson.categories.map((cat) => ({
  id: cat.id as FAQCategory,
  label: cat.label,
  icon: cat.icon,
}));

const faqData: FAQItem[] = faqDataJson.faqs.map((faq) => ({
  id: faq.id,
  question: faq.question,
  answer: faq.answer,
  category: faq.category as Exclude<FAQCategory, 'all'>,
}));

/**
 * Recherche, filtres et accordéon de la FAQ.
 *
 * Isolé du reste de la page, qui est statique et rendu côté serveur : seule
 * cette partie a besoin de JavaScript.
 */
export default function FaqSearch() {
  const [activeCategory, setActiveCategory] = useState<FAQCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const baseId = useId();

  const toggleItem = (id: number) => {
    const next = new Set(openItems);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenItems(next);
  };

  const filteredFAQs = faqData.filter((faq) => {
    const matchesCategory =
      activeCategory === 'all' || faq.category === activeCategory;
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query);
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      {/* Recherche */}
      <div className="container mx-auto px-4 -mt-8 relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <label htmlFor={`${baseId}-search`} className="sr-only">
              Rechercher une question
            </label>
            <input
              id={`${baseId}-search`}
              type="search"
              placeholder="Recherchez votre question..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 pr-12 rounded-full border-2 border-gray-200 focus:border-c2e-blue focus:ring-4 focus:ring-c2e-blue/10 outline-none transition-all shadow-xl bg-white text-gray-900"
            />
            <Search
              className="absolute right-5 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {/* Filtres par catégorie */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap justify-center gap-3">
          {categories.map((cat) => {
            const IconComponent = iconMap[cat.icon as keyof typeof iconMap];
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setSearchQuery('');
                }}
                className={`px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2 ${
                  isActive
                    ? 'bg-c2e-blue text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                }`}
              >
                {IconComponent && (
                  <IconComponent className="h-5 w-5" aria-hidden="true" />
                )}
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Questions */}
      <div className="container mx-auto px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          {/* Annonce le nombre de résultats aux lecteurs d'écran, qui ne
              voient pas la liste se réduire pendant la frappe. */}
          <p className="sr-only" role="status" aria-live="polite">
            {filteredFAQs.length} question
            {filteredFAQs.length > 1 ? 's' : ''} affichée
            {filteredFAQs.length > 1 ? 's' : ''}
          </p>

          {filteredFAQs.length > 0 ? (
            <div className="space-y-4">
              {filteredFAQs.map((faq) => {
                const isOpen = openItems.has(faq.id);
                const panelId = `${baseId}-panel-${faq.id}`;
                const buttonId = `${baseId}-button-${faq.id}`;
                return (
                  <div
                    key={faq.id}
                    className={`bg-white rounded-2xl shadow-sm border-2 transition-all ${
                      isOpen
                        ? 'border-c2e-blue shadow-lg'
                        : 'border-transparent hover:border-gray-200'
                    }`}
                  >
                    <h3>
                      <button
                        id={buttonId}
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleItem(faq.id)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-c2e-blue"
                      >
                        <span className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </span>
                        <ChevronDown
                          className={`h-6 w-6 text-c2e-blue shrink-0 transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      hidden={!isOpen}
                      className="px-6 pb-5 pt-2 border-t border-gray-100"
                    >
                      {/* `pre-line` : les \n saisis dans faq.json deviennent
                          des retours à la ligne — pour les réponses en étapes. */}
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <SearchX
                className="h-16 w-16 mx-auto mb-4 text-gray-300"
                aria-hidden="true"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Aucun résultat trouvé
              </h3>
              <p className="text-gray-600 mb-6">
                Essayez avec d&apos;autres mots-clés ou consultez toutes nos
                questions fréquentes
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                }}
                className="px-6 py-3 bg-c2e-blue text-white rounded-full font-semibold hover:bg-c2e-blue-light transition-colors"
              >
                Voir toutes les FAQ
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
